import React, {useState} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {Icon} from '@drivesally/sally-ui';
import {darkGrey, green, grey, red} from '@drivesally/sally-ui/lib/libs/colors';
import {dateTime} from '@drivesally/sally-fw/lib/type';

import displayTimeZoneSpecificDate from '../../../libs/displayTimeZoneSpecificDate';
import formatCallDuration from '../../../aircall/lib/formatCallDuration.lib.aircall';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 6px;
  width: 100%;
`;

const CallRow = styled.div`
  align-items: baseline;
  color: ${darkGrey};
  display: flex;
  font-size: 12px;
  gap: 6px;
  line-height: 18px;
`;

const Direction = styled.span`
  color: ${({inbound}) => (inbound ? red : green)};
  flex-shrink: 0;
  font-size: 11px;
`;

const CallMeta = styled.span`
  min-width: 0;
  overflow-wrap: break-word;
`;

const PlayLink = styled.a`
  align-items: center;
  color: #888;
  display: flex;
  flex-shrink: 0;
  text-decoration: none;

  &:hover {
    color: ${darkGrey};
  }
`;

const ToggleButton = styled.button`
  align-items: center;
  align-self: flex-start;
  background: none;
  border: none;
  color: #888;
  cursor: pointer;
  display: flex;
  font-size: 12px;
  gap: 4px;
  padding: 0;

  &:hover {
    color: ${darkGrey};
  }
`;

const Empty = styled.div`
  color: #888;
  font-size: 12px;
  margin-top: 6px;
`;

const Separator = styled.span`
  color: ${grey};
`;

const CrmAircallLog = ({branch, calls, loading}) => {
  const [expanded, setExpanded] = useState(false);

  if (loading) return <Empty>Loading calls...</Empty>;
  if (!calls.length) return <Empty>No recent calls.</Empty>;

  const visibleCalls = expanded ? calls : calls.slice(0, 1);
  const hiddenCount = calls.length - 1;

  return (
    <Wrapper>
      {visibleCalls.map((call) => {
        const inbound = call.direction === 'inbound';
        return (
          <CallRow key={call.id}>
            <Direction inbound={inbound} title={inbound ? 'Inbound' : 'Outbound'}>
              <Icon icon={inbound ? 'arrowdown' : 'arrowup'} />
            </Direction>
            <CallMeta>
              {displayTimeZoneSpecificDate({
                date: call.started_at,
                type: dateTime,
                branch,
              })}
              <Separator> | </Separator>
              {formatCallDuration(call.duration)}
              {!!call.user_name && (
                <>
                  <Separator> | </Separator>
                  {call.user_name}
                </>
              )}
            </CallMeta>
            {!!call.recording && (
              <PlayLink
                href={call.recording}
                target="_blank"
                rel="noopener noreferrer"
                title="Open recording"
              >
                <Icon icon="play" />
              </PlayLink>
            )}
          </CallRow>
        );
      })}
      {hiddenCount > 0 && (
        <ToggleButton type="button" onClick={() => setExpanded((open) => !open)}>
          <Icon icon={expanded ? 'caretup' : 'caretdown'} />
          {expanded ? 'Hide calls' : `${hiddenCount} more call${hiddenCount > 1 ? 's' : ''}`}
        </ToggleButton>
      )}
    </Wrapper>
  );
};

CrmAircallLog.propTypes = {
  branch: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  calls: PropTypes.array,
  loading: PropTypes.bool,
};

CrmAircallLog.defaultProps = {
  calls: [],
};

export default CrmAircallLog;
